import { debounce } from '@js/util/helpers'
import { derived, signal } from './signals'

/**
 * @param {import('alpinejs').Alpine} Alpine
 */
export default Alpine => {
  Alpine.directive('swiper', (el, { expression }, { evaluate, evaluateLater, cleanup }) => {
    const { items } = evaluate(expression)
    const scrollerElement = evaluate('$refs.scroller')
    const listElement = evaluate('$refs.list')

    if (!Number.isInteger(items)) {
      console.warn('Cannot initialize swiper: "items" option must be an integer')
      return
    }

    if (!scrollerElement || !listElement) {
      console.warn('Cannot initialize swiper: missing elements')
      return
    }

    // Define public API
    const [getIndex, setIndex] = signal(0)
    const [getColumns, setColumns] = signal(1)
    const isFirst = derived(() => getIndex() <= 0)
    const isLast = derived(() => getIndex() >= items - getColumns())

    const $data = evaluate('$data')
    evaluate(() => {
      $data.swiper = {
        get columns() {
          return getColumns()
        },
        get index() {
          return getIndex()
        },
        get isAtFirst() {
          return isFirst()
        },
        get isAtLast() {
          return isLast()
        },
        goto(index) {
          if (isNaN(index)) return
          if (index < 0 || index >= this.itemsCount) return

          // This should not be needed, but Safari 15 reports a wrong offsetLeft for all elements
          const firstOffset = listElement.children[0].offsetLeft

          scrollerElement.scrollTo({
            left: listElement.children[index].offsetLeft - firstOffset,
            behavior: 'smooth',
          })
        },
        prev() {
          this.goto(this.index - 1)
        },
        next() {
          this.goto(this.index + 1)
        },
      }
    })

    // Handle sizing
    const detectColumns = () =>
      Number(getComputedStyle(listElement).getPropertyValue('--swiper-columns')) || 1
    const observer = new ResizeObserver(() => {
      setColumns(detectColumns())
      setIndex(detectIndex())
    })
    observer.observe(listElement)
    cleanup(() => {
      observer.disconnect()
    })

    // Handle scrolling
    function detectIndex() {
      const scrollerOffset = scrollerElement.getBoundingClientRect().left
      for (const child of listElement.children) {
        const childOffset = child.getBoundingClientRect().left + 10
        if (childOffset >= scrollerOffset) return Number(child.dataset.index)
      }
      return 0
    }
    const onScrollend = () => setIndex(detectIndex())
    const onScroll = debounce(200, onScrollend)
    if ('onscrollend' in scrollerElement) {
      scrollerElement.addEventListener('scrollend', onScrollend)
    } else {
      scrollerElement.addEventListener('scroll', onScroll)
    }
    cleanup(() => {
      scrollerElement.removeEventListener('scrollend', onScrollend)
      scrollerElement.removeEventListener('scroll', onScroll)
    })
  })
}
